import styled from 'styled-components'

import { Button, NavigationWrapper } from '@base'

import { CreateShipmentWrapper, EditShipmentRightSection, FormRow, FormWrapper, SectionBlock } from '@pages/Admin/Orders/ManageOrder/Shipment/styles'
import { Devices, Spacings, Text } from '@styles'

export const DispatchPlanNavigationWrapper = styled(NavigationWrapper)`
	display: flex;
	align-items: center;
	justify-content: space-between;
`
export const ButtonsWrapper = styled.div`
	display: flex;
	justify-content: end;
	gap: ${Spacings.SPACING_2B};
`

export const SearchWrapper = styled.div`
	margin-left: auto;
`

export const CopySpan = styled.span`
	cursor: copy;
`

export const InvoiceCardWrapper = styled.div`
	display: flex;
	flex-direction: column;
	margin-bottom: ${Spacings.SPACING_3B};
`

export const DeleteInvoiceButtonWrapper = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: end;
	gap: ${Spacings.SPACING_2B};
`

export const InputWrapper = styled.div`
	padding: ${Spacings.SPACING_3B};
`

export const PaymentListWrapper = styled.div`
	margin-bottom: ${Spacings.SPACING_3B};
`

export const StatusWrapper = styled.div``

export const TabsWrapper = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	margin: ${Spacings.SPACING_4B} 0;
`

export const EnquiryLink = styled.div`
	color: ${({ theme }) => theme.palette.text.blue};
`
export const PopupButton = styled(Button)`
	min-width: auto;
	border-color: ${({ theme }) => theme.palette.text.blue};
	& > svg {
		margin-left: ${Spacings.SPACING_2B};
	}
`
export const PopUpButtonWrapper = styled.div`
	display: flex;
	flex-direction: column;
	min-width: 8rem;
	gap: ${Spacings.SPACING_2B};
`
export const TableContainer = styled.div`
	padding: ${Spacings.SPACING_4B};
	background: white;
	border-radius: ${Spacings.SPACING_2B};
	display: flex;
	flex-direction: column;
	margin-bottom: ${Spacings.SPACING_4B};
	flex-grow: 1;
	box-shadow: 0 ${Spacings.SPACING_1B} ${Spacings.SPACING_5B} 0 rgb(0 0 0 / 5%);
`

export const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	min-height: 100%;
	gap: ${Spacings.SPACING_2B};
	padding-bottom: ${Spacings.SPACING_2B};
`

export const Label = styled.label`
	font-size: ${Text.MEDIUM};
	margin-left: ${Spacings.SPACING_2B};
	color: ${({ theme }) => theme.palette.text.black};
	font-weight: ${Text.BOLD_600};
	cursor: ${({ disabled }) => (disabled ? 'initial' : 'pointer')};
	user-select: none;
`

export const RadioButtonWrapper = styled.div`
	display: flex;
	align-items: center;
	flex-grow: 1;
	padding: ${Spacings.SPACING_2B} 0;
`

export const RadioButtonsWrapper = styled.div`
	display: flex;
	flex-direction: row;
`

export const ButtonWapper = styled.div`
	display: flex;
	gap: ${Spacings.SPACING_2B};
`

// full-width bar above the left/right sections of Add Dispatch Plan, on the same card surface as them
export const AddDispatchPlanHeader = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: ${Spacings.SPACING_3B};
	flex: 0 0 auto;
	padding: ${Spacings.SPACING_3B};
	background: ${({ theme }) => theme.colors.surface.card};
	border: 1px solid ${({ theme }) => theme.colors.border.default};
	border-radius: ${({ theme }) => theme.radius.lg};

	/* the plan select: full width, 60% from laptop up */
	& > div:first-child {
		width: 100%;
	}

	@media ${Devices.tablet} {
		padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_4B};
	}

	@media ${Devices.laptop} {
		flex-wrap: nowrap;

		& > div:first-child {
			width: 60%;
		}
	}
`

// logistics handler | partner, common to every dispatch tab: side by side, taking the rest of the header row
export const AddDispatchPlanLogistics = styled.div`
	display: grid;
	grid-template-columns: minmax(0, 1fr) minmax(0, 2fr) auto;
	align-items: end;
	gap: ${Spacings.SPACING_3B};
	flex: 1 1 100%;

	@media ${Devices.laptop} {
		flex-basis: 0;
	}
`

// the dispatch form is only a row tall, so it must not clip the date picker popup opening below it
export const DispatchFormWrapper = styled(FormWrapper)`
	overflow: visible;
`

export const VehicleSectionHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${Spacings.SPACING_3B};
	padding-top: ${Spacings.SPACING_2B};
	border-top: 1px solid ${({ theme }) => theme.colors.border.default};
`

// the right side: the plan details and the vehicle details as two separate cards
export const RightCards = styled(EditShipmentRightSection)`
	gap: ${Spacings.SPACING_3B};
	/* the date picker popup must pop out of the card instead of being clipped (and scrolling it) */
	overflow: visible;
	max-height: none;
`

// the dispatch tabs + date card: SectionBlock scrolls from laptop up, which clips the date picker popup
export const DispatchSectionBlock = styled(SectionBlock)`
	overflow: visible;
`

// Takes the height the page has left and scrolls inside it, with the scrollbar hidden. Spelling it
// out here (rather than leaning on CreateShipmentWrapper) keeps the page from ever growing past its
// box, which is what pushed the action bar off the bottom. The date card inside keeps its own
// overflow visible, so the picker is only bounded by this grid — and it opens well inside it.
export const DispatchPlanGrid = styled(CreateShipmentWrapper)`
	flex: 1 1 0;
	min-height: 0;
	overflow-y: auto;
	overflow-x: hidden;

	&::-webkit-scrollbar {
		display: none;
	}
	-ms-overflow-style: none;
	scrollbar-width: none;
`

// the vehicle details card has its own surface, so its heading needs no divider
export const VehicleCardHeader = styled(VehicleSectionHeader)`
	padding-top: 0;
	border-top: none;
`

// one bordered card per vehicle: its inputs with the remove icon at the end
export const VehicleCard = styled.div`
	padding: ${Spacings.SPACING_3B};
	border: 1px solid ${({ theme }) => theme.colors.border.default};
	border-radius: ${({ theme }) => theme.radius.lg};
`

// vehicle number | driver number | quantity | remove, stacked on mobile
export const VehicleFields = styled.div`
	display: grid;
	grid-template-columns: 1fr;
	align-items: flex-start;
	gap: ${Spacings.SPACING_3B};

	@media ${Devices.tablet} {
		grid-template-columns: repeat(3, minmax(0, 1fr)) auto;
	}
`

// an empty label-sized line on top, so the remove icon sits level with the input boxes
export const VehicleRemoveCell = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: ${Spacings.SPACING_1B};
	justify-self: end;

	&::before {
		content: '\\00a0';
		font-size: ${Text.EXTRA_SMALL};
		font-weight: ${Text.BOLD_600};
	}
`

// takes the input's padding, font and border so it is as tall as the input box, with the icon centred in it
export const VehicleRemoveButton = styled.button`
	position: relative;
	aspect-ratio: 1;
	padding: ${Spacings.SPACING_6} 0;
	border: 1px solid transparent;
	border-radius: ${({ theme }) => theme.radius.full};
	background: transparent;
	color: ${({ theme }) => theme.colors.text.secondary};
	font-family: inherit;
	font-size: ${Text.SMALL};
	cursor: pointer;
	transition: background 0.15s ease;

	@media ${Devices.tablet} {
		padding: ${Spacings.SPACING_2B} 0;
		font-size: ${Text.MEDIUM};
	}

	&:hover {
		background: ${({ theme }) => theme.colors.border.default};
	}

	&::before {
		content: '\\200b';
	}

	svg {
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		font-size: ${Text.X_LARGE};
	}
`

// the dispatch fields above the vehicle details: date | quantity
export const DispatchFieldsRow = styled(FormRow)`
	width: 100%;
	grid-template-columns: repeat(2, minmax(0, 1fr));
`
// v2 Calendar in the header row: fixed width (it is width: 100% by default), the "Date" label floated above so
// only the date box takes part in the row's alignment, and the box matched to the v1 header button height.
export const CalendarWrapper = styled.div`
	position: relative;
	width: 10rem;

	label {
		position: absolute;
		bottom: calc(100% + ${Spacings.SPACING_1B});
		left: 0;
		white-space: nowrap;
	}

	input {
		height: 2.125rem;

		@media ${Devices.tablet} {
			height: 2.5rem;
		}
	}
`
